export default `vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec4 permute(vec4 x) { return mod289(((x * 34.0) + 1.0) * x); }
vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

float snoise(vec3 v) {
  const vec2 C = vec2(1.0 / 6.0, 1.0 / 3.0);
  const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);

  vec3 i  = floor(v + dot(v, C.yyy));
  vec3 x0 = v - i + dot(i, C.xxx);

  vec3 g = step(x0.yzx, x0.xyz);
  vec3 l = 1.0 - g;
  vec3 i1 = min(g.xyz, l.zxy);
  vec3 i2 = max(g.xyz, l.zxy);

  vec3 x1 = x0 - i1 + C.xxx;
  vec3 x2 = x0 - i2 + C.yyy;
  vec3 x3 = x0 - D.yyy;

  i = mod289(i);
  vec4 p = permute(permute(permute(
            i.z + vec4(0.0, i1.z, i2.z, 1.0))
          + i.y + vec4(0.0, i1.y, i2.y, 1.0))
          + i.x + vec4(0.0, i1.x, i2.x, 1.0));

  float n_ = 0.142857142857;
  vec3 ns = n_ * D.wyz - D.xzx;

  vec4 j = p - 49.0 * floor(p * ns.z * ns.z);

  vec4 x_ = floor(j * ns.z);
  vec4 y_ = floor(j - 7.0 * x_);

  vec4 x = x_ * ns.x + ns.yyyy;
  vec4 y = y_ * ns.x + ns.yyyy;
  vec4 h = 1.0 - abs(x) - abs(y);

  vec4 b0 = vec4(x.xy, y.xy);
  vec4 b1 = vec4(x.zw, y.zw);

  vec4 s0 = floor(b0) * 2.0 + 1.0;
  vec4 s1 = floor(b1) * 2.0 + 1.0;
  vec4 sh = -step(h, vec4(0.0));

  vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;
  vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;

  vec3 p0 = vec3(a0.xy, h.x);
  vec3 p1 = vec3(a0.zw, h.y);
  vec3 p2 = vec3(a1.xy, h.z);
  vec3 p3 = vec3(a1.zw, h.w);

  vec4 norm = taylorInvSqrt(vec4(dot(p0, p0), dot(p1, p1), dot(p2, p2), dot(p3, p3)));
  p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;

  vec4 m = max(0.6 - vec4(dot(x0, x0), dot(x1, x1), dot(x2, x2), dot(x3, x3)), 0.0);
  m = m * m;
  return 42.0 * dot(m * m, vec4(dot(p0, x0), dot(p1, x1), dot(p2, x2), dot(p3, x3)));
}

float fbm(vec3 p, int octaves) {
  float sum = 0.0;
  float amp = 0.5;
  float freq = 1.0;
  for (int i = 0; i < 8; i++) {
    if (i >= octaves) break;
    sum += amp * snoise(p * freq);
    freq *= 2.0;
    amp *= 0.5;
  }
  return sum;
}

vec3 snoiseVec3(vec3 p) {
  return vec3(snoise(p), snoise(p + 19.19), snoise(p + 113.7));
}

vec3 curlNoise(vec3 p) {
  const float e = 0.1;
  vec3 dx = vec3(e, 0.0, 0.0);
  vec3 dy = vec3(0.0, e, 0.0);
  vec3 dz = vec3(0.0, 0.0, e);

  vec3 px0 = snoiseVec3(p - dx), px1 = snoiseVec3(p + dx);
  vec3 py0 = snoiseVec3(p - dy), py1 = snoiseVec3(p + dy);
  vec3 pz0 = snoiseVec3(p - dz), pz1 = snoiseVec3(p + dz);

  float x = (py1.z - py0.z) - (pz1.y - pz0.y);
  float y = (pz1.x - pz0.x) - (px1.z - px0.z);
  float z = (px1.y - px0.y) - (py1.x - py0.x);

  return normalize(vec3(x, y, z) / (2.0 * e) + 1e-6);
}
float lightAt(vec2 nuv) {
  vec2 src = vec2(1.02, 1.06); 
  float d = distance(nuv, src);
  return clamp(1.15 - d * 0.92, 0.0, 1.2);
}

float grazing(vec3 normal, vec3 lightDir) {
  float diff = max(dot(normalize(normal), normalize(lightDir)), 0.0);
  return clamp(diff * 0.8 + 0.2, 0.0, 1.0);
}

attribute vec3  aNormal;
attribute vec3  aScatter; 
attribute float aSeed;

uniform float uReveal;   
uniform float uFocus;    
uniform float uTime;
uniform float uSize;
uniform float uScale;
uniform float uScatter;  
uniform float uPixelRatio;
uniform float uGlyphCount;
uniform float uCullBack; 
uniform vec3  uLightDir;

varying float vGlyph;
varying float vBright;
varying float vAlpha;

void main() {
  float r = clamp(uReveal, 0.0, 1.0);
  float er = r * r * (3.0 - 2.0 * r);

  vec3 scattered = position + aScatter * uScatter;
  vec3 flow = curlNoise(position * 0.3 + aSeed * 5.0 + vec3(0.0, 0.0, uTime * 0.03));
  scattered += flow * (1.0 - er) * uScatter * 0.5;
  vec3 pos = mix(scattered, position, er);

  vec4 mv = modelViewMatrix * vec4(pos, 1.0);

  
  
  
  if (uCullBack > 0.5) {
    vec3 vn = normalize(normalMatrix * aNormal);
    
    
    if (dot(vn, normalize(-mv.xyz)) < 0.08) {
      gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
      gl_PointSize = 0.0;
      return;
    }
  }

  gl_Position = projectionMatrix * mv;

  gl_PointSize = uSize * uPixelRatio * (uScale / max(-mv.z, 0.1))
                 * (0.55 + 0.45 * er) * (0.85 + 0.3 * uFocus);

  
  float grz = grazing(aNormal, uLightDir);
  float flick = 0.9 + 0.1 * sin(uTime * 1.3 + aSeed * 30.0);
  vBright = clamp((0.22 + grz * 0.7) * (0.55 + 0.55 * uFocus) * flick, 0.0, 1.0);
  vGlyph = floor(vBright * (uGlyphCount - 1.0) + 0.5);
  
  vAlpha = (0.18 + 0.82 * er) * (0.5 + 0.5 * uFocus);
}`